import React from 'react';
import { useSelector } from 'react-redux';
import { MenuItem } from '@mui/material';
import { Order, AwardingRuleParameterType } from '../../../../Enums';
import { useTranslation } from 'react-i18next';
import StyledSelect from '../StyledSelect';
import InputTitle from '../InputTitle';

import { noop, getHyperfindPriorityLabel } from './HyperfindParameter.service';
import './Step2-SortingRuleSection.scss';

/**
 * @param {{
 *  currentAwardingRule: ApiAdmin.IBodyAwardingRules,
 *  onChange: Function,
 *  isDisabled: boolean,
 *  fieldValidationErrors: {[Key: string] : boolean},
 * }} props
 */
function HyperfindParameter(props) {
    const { t: tSortingRules } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.sortingRuleStep' });
    const {
        currentAwardingRule,
        onChange = noop,
        isDisabled,
        fieldValidationErrors,
    } = props;

    /** @type {AdminAppData.IAppData} */
    const adminAppData = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminAppData);
    /** @type {RuleSet.IRuleSetDetails} */
    const adminRuleSet = useSelector((/** @type {EmploymentBillboard.AdminStore} */ state) => state.adminRuleSet);
    const { awardingRules } = adminRuleSet;
    const { hyperfinds } = adminAppData;
    const { hyperfindId } = currentAwardingRule.hyperfindBelongingInfos;

    /**
    * @typedef {{value: string, label: string, disabled: boolean}} SelectDataviewFieldOption
    * @type {SelectDataviewFieldOption[]}
    */
    const hyperfindOptions = hyperfinds.map((x) => ({
        value: x.value,
        label: x.label,
        disabled: awardingRules.findIndex((parameter) => parameter.type === AwardingRuleParameterType.HyperfindBelonging && parameter.hyperfindBelongingInfos.hyperfindId === x.value) !== -1,
    })) || [];

    /**
     * @typedef {{value: Order, label: string}} SelectOption
     * @type {SelectOption[]}
     */
    const orderOptions = Object.values(Order).map((o) => ({
        value: o, label: getHyperfindPriorityLabel(o),
    }));

    const selectedHyperfind = hyperfindOptions.find((x) => x.value === hyperfindId);
    const selectedOrder = orderOptions.find((x) => x.value === currentAwardingRule.order);

    return (
        <div className="srs--dataviewCustomHyperfind-container">
            <div className="rse--selection-field-container srs-hyperfind-container">
                <InputTitle>{tSortingRules('hyperfindBelonging')}</InputTitle>
                <StyledSelect
                    labelId="select-hyperfind-label"
                    id="select-hyperfind-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => selectedHyperfind?.label}
                    value={selectedHyperfind?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => onChange('hyperfindId', event.target.value)}
                >
                    {hyperfindOptions.map((x) => (
                        <MenuItem
                            value={x.value}
                            key={`SelectItem_${x.label}`}
                            disabled={x.disabled}
                        >
                            {x.label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {fieldValidationErrors.hyperfindId && <span style={{ color: 'red' }}>{tSortingRules('selectHyperfind')}</span>}
            </div>
            <div className="rse--selection-field-container srs-dataAndOrderType-container">
                <InputTitle>{tSortingRules('priority')}</InputTitle>
                <StyledSelect
                    labelId="select-sort-order-type-label"
                    id="select-sort-order-type-type"
                    disabled={isDisabled}
                    renderValue={(/** @type {string} */
                        _value,
                    ) => selectedOrder?.label}
                    value={selectedOrder?.value ?? ''}
                    onChange={(/** @type {React.ChangeEvent<{ name?: string; value: string }>} */
                        event,
                    ) => onChange('order', event.target.value)}
                >
                    {orderOptions.map(({ label, value }) => (
                        <MenuItem
                            value={value}
                            key={`SelectItem_${label}`}
                        >
                            {label}
                        </MenuItem>
                    ))}
                </StyledSelect>
                {fieldValidationErrors.order && <span style={{ color: 'red' }}>{tSortingRules('selectSortingOrder')}</span>}
            </div>
        </div>
    );
}

export default HyperfindParameter;